import { ControllerHelper, GroupController, UserShortInfoDownloadDto } from 'collaboration-service';
import CancelWithConfirmation from 'components/CancelWithConfirmation';
import UserSearch from 'components/UserSearch/UserSearch';
import { Button, ImgSelect, Label, Theme } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { getTranslated, getTranslatedUserName } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { ThemeContext } from 'styled-components';
import { CMSSC as T } from 'views/CMS/CMSSC';

export type RoleMap = Array<{ user: UserShortInfoDownloadDto, roles: string[], confirm?: boolean }>;

export interface UserAccessManagementProps {
    groupId: string;
    roles: RoleMap;
    setRoles: React.Dispatch<React.SetStateAction<RoleMap>>;
    responsibleUsers?: UserShortInfoDownloadDto[];
    onResponsibleUsersChange?: (users: UserShortInfoDownloadDto[]) => void;
    mayAssignRoles?: boolean;
    renderMissingMembers?: React.ReactNode;
    renderMissingResponsible?: React.ReactNode;
    renderMissingUser?: (userId: string) => React.ReactNode;
    onAllRolesLoaded?: () => void;
    children?: React.ReactNode;
    namespace?: string;
}

export const UserAccessManagement = (p: UserAccessManagementProps) => {
    const {
        groupId, roles, setRoles,
        responsibleUsers, onResponsibleUsersChange,
        mayAssignRoles = true,
        renderMissingMembers, renderMissingResponsible, renderMissingUser,
        onAllRolesLoaded,
        children,
        namespace,
    } = p;

    const theme = React.useContext<Theme>(ThemeContext);
    const { t } = useImgI18N(namespace ?? "cms");

    const [allRoles, setAllRoles] = React.useState<Array<{ value: string, label: string }>>();

    React.useEffect(() => {
        const load = async () => {
            const r = await ControllerHelper
                .singleCall({ groupId }, GroupController.GetGroupRoles, true);
            const mappedRoles = _.map(r?.definedRoles ?? [], (v, k) => ({ label: getTranslated(v.name)?.text ?? k, value: k }));
            setAllRoles(mappedRoles);
            onAllRolesLoaded?.();
        };
        if (groupId !== "")
            load();
    }, [groupId, onAllRolesLoaded]);

    const toHide = React.useMemo(() => _.map(roles, r => r.user.id), [roles]);

    const onMembers = React.useCallback((users: UserShortInfoDownloadDto[]) => {
        setRoles(prev => {
            const newRoles = _.clone(prev);
            newRoles.push({ user: _.isArray(users) ? users[0] : users, roles: [] });
            return newRoles;
        });
    }, [setRoles]);

    return (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(147px, max-content) 1fr", columnGap: 10, width: "100%", rowGap: 5 }}>
            <T.Label>
                <Label icon="user plus" content={t("add member")} bold />
                {renderMissingMembers}
            </T.Label>
            <div style={{ paddingLeft: 29 }}>
                <UserSearch
                    searchInfo={{ group: groupId }}
                    users={[]}
                    usersSelected={onMembers}
                    showAvatar
                    toHide={toHide}
                /></div>
            <T.Label>
                <Label icon="users" content={t("member roles")} bold />
                {renderMissingResponsible}
            </T.Label>
            {roles.length > 0 ?
                <div>
                    {_.map(roles, r => {
                        if (r.confirm === undefined)
                            r.confirm = false;
                        const value = _.filter(_.map(r.roles, rr => _.find(allRoles, m => m.value === rr)!), f => f !== undefined);
                        const updatedRoles = (value: ReadonlyArray<{ value: string, label: string }>) => {
                            const newRoles = _.clone(roles);
                            const rr = _.find(newRoles, nr => nr.user.id === r.user.id);
                            if (rr) {
                                rr.roles = _.map(value, v => v.value);
                                setRoles(newRoles);
                            }
                        }
                        const hasResponsible = responsibleUsers !== undefined && onResponsibleUsersChange !== undefined;
                        const respIdx = hasResponsible ? _.findIndex(responsibleUsers, u => u.id === r.user.id) : -1;
                        const resp = respIdx >= 0;
                        const toggleResp = () => {
                            if (!hasResponsible) return;
                            const ru = _.clone(responsibleUsers);
                            const idx = _.findIndex(ru, u => u.id === r.user.id);
                            if (idx >= 0)
                                ru.splice(idx, 1);
                            else
                                ru.push(r.user);
                            onResponsibleUsersChange(ru);
                        }
                        // const flipConfirm = () => {
                        //     r.confirm = !r.confirm;
                        //     setRoles(_.clone(roles));
                        // };
                        const removeUser = () => {
                            const newRoles = _.filter(_.clone(roles), f => f.user.id !== r.user.id);
                            setRoles(newRoles);
                            if (hasResponsible) {
                                const ru = _.clone(responsibleUsers);
                                const idx = _.findIndex(ru, u => u.id === r.user.id);
                                if (idx >= 0) {
                                    ru.splice(idx, 1);
                                    onResponsibleUsersChange(ru);
                                }
                            }
                        };

                        return (
                            <T.ProjectRolesGrid key={r.user.id} confirmed={r.confirm}>
                                <T.RolesUser
                                    title={getTranslatedUserName(r.user)}
                                    style={{
                                        fontWeight: value.length > 0 ? "inherit" : "bolder",
                                        color: value.length > 0 ? "inherit" : theme.colors.accentRed,
                                        lineHeight: "40px",
                                        transform: "translateX(-21px)",
                                    }}
                                >
                                    {getTranslatedUserName(r.user)}
                                    {renderMissingUser?.(r.user.id)}
                                </T.RolesUser>
                                <T.RolesIcon style={{ zIndex: 20 }}>
                                    {/* <Button
                                        floated='right'
                                        kind={r.confirm ? "secondary" : "cancelButton"}
                                        icon={r.confirm ? "times" : "user minus"}
                                        onClick={value.length > 0 ? flipConfirm : removeUser}
                                        tooltip={{
                                            tooltipText: r.confirm ? t("cancel") : t("remove user from list"),
                                            position: r.confirm ? "top" : "right"
                                        }}
                                    />
                                    {r.confirm &&
                                        <Button floated='right' kind="primary" icon="user minus" onClick={removeUser}
                                            tooltip={{ tooltipText: t("yes, remove user from list") }}
                                        />
                                    } */}
                                    <CancelWithConfirmation
                                        floated='right'
                                        cancel={removeUser}
                                        iconOnly
                                        icon='user minus'
                                        openAbsolute
                                        kind='halfTransparentButton'
                                        confirmText={t("yes, remove user from list")}
                                        namespace='playlist'
                                        cancelText='cancel'
                                        tooltipInitialButton={t("remove user from list")}
                                    />
                                </T.RolesIcon>
                                {hasResponsible &&
                                    <T.Responsible style={{ transform: "translateX(-21px)" }}>
                                        <Button
                                            floated='right'
                                            kind={"fullTransparentButton"}
                                            icon={resp ? "circle dot" : "circle"}
                                            iconColor={resp ? "@accent" : undefined}
                                            onClick={toggleResp}
                                            tooltip={{
                                                tooltipText: resp ? t("user is project responsible") : t("user is normal project member"),
                                                position: "right"
                                            }}
                                        />
                                    </T.Responsible>
                                }
                                <T.RolesRoles style={{ paddingLeft: 10 }}>
                                    <ImgSelect
                                        value={value}
                                        options={allRoles}
                                        onChange={updatedRoles}
                                        isMulti
                                        isDisabled={!mayAssignRoles}
                                    />
                                </T.RolesRoles>
                            </T.ProjectRolesGrid>
                        );
                    })}
                </div>
                :
                <T.Label style={{ paddingLeft: 29 }}>{t("you have to add at least one member")}</T.Label>
            }
            {children}
        </div>
    );
};

export default UserAccessManagement;
